import { useMemo, useRef } from "react";

import { ChevronDown } from "lucide-react";

import * as Combobox from "@/components/ui/combobox";
import { ULThemeButton } from "@/components/ULThemeButton";
import { cn } from "@/lib/utils";
import type { CountryCodePickerData } from "@/utils/helpers/countryUtils";
import { getAllCountryCodeOptions } from "@/utils/helpers/countryUtils";

export interface ULThemeCountryCodeDropdownProps {
  /**
   * Currently selected country.
   */
  selectedCountry?: CountryCodePickerData;
  /**
   * Called with the picked country when the user selects one from the list.
   */
  onSelect: (country: CountryCodePickerData) => void;
  /**
   * Placeholder text when no country is selected.
   */
  placeholder?: string;
  /**
   * Placeholder text for the search input inside the popup.
   */
  searchPlaceholder?: string;
  /**
   * Full width styling.
   */
  fullWidth?: boolean;
  /**
   * When true, shows only the dial code (e.g. "+47") instead of the full
   * country name and code.
   */
  compact?: boolean;
  /**
   * Optional label rendered above the picker, matching the static label
   * used above adjacent fields (e.g. the phone number field).
   */
  label?: string;
  /**
   * BCP 47 locale used to localize country names and their sort order
   * (e.g. "no", "en"), matching how Auth0's own country picker behaves.
   */
  locale?: string;
  className?: string;
  disabled?: boolean;
}

function ULThemeCountryCodeDropdown({
  selectedCountry,
  onSelect,
  placeholder = "Select Country",
  searchPlaceholder = "Search...",
  fullWidth = false,
  compact = false,
  label,
  locale,
  className,
  disabled,
}: ULThemeCountryCodeDropdownProps) {
  const countryOptions = useMemo(() => getAllCountryCodeOptions(locale), [locale]);
  // Combobox.Portal appends to document.body by default, which escapes the
  // .hotels-theme/.theme-universal scoped ancestor the rest of this card
  // relies on for its font-family and theme-universal:* utilities. Mounting
  // the portal inside this component's own (in-tree) container keeps the
  // popup themed the same as everything else.
  const containerRef = useRef<HTMLDivElement>(null);
  const widthStyles = fullWidth ? "w-full" : "";
  const disabledStyles = disabled ? "disabled:opacity-70 cursor-not-allowed" : "cursor-pointer";

  // ULThemeButton's outline variant hardcodes p-6, which overrides its own h-10
  // and renders taller than the h-14 input fields it needs to sit flush with.
  // className can't win that fight (ULThemeButton's own overrides always merge in
  // after it), so pin the height/vertical padding here via inline style instead.
  const compactStyle = compact
    ? {
        height: "3.5rem",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: "0.75rem",
        paddingRight: "0.75rem",
      }
    : undefined;

  return (
    <div ref={containerRef}>
      {label && (
        <label className="mb-2 block text-sm theme-universal:font-bold theme-universal:text-body-text theme-universal:text-(length:--ul-theme-font-body-text-size)">
          {label}
        </label>
      )}
      <Combobox.Root<CountryCodePickerData>
        items={countryOptions}
        value={selectedCountry}
        onValueChange={(value) => {
          if (value) {
            onSelect(value);
          }
        }}
        isItemEqualToValue={(item, value) => item.code === value.code}
        itemToStringLabel={(item) => item.name}
      >
        <ULThemeButton
          render={<Combobox.Trigger />}
          type="button"
          variant="outline"
          disabled={disabled}
          style={compactStyle}
          className={cn(
            // Override ULThemeButton styles for country picker specific styling
            compact ? "justify-center" : "justify-between",
            "text-left font-medium",
            "theme-universal:bg-input-bg",
            "theme-universal:text-input-text",
            "theme-universal:border-input-border",
            "theme-universal:text-(length:--ul-theme-font-body-text-size)",
            "theme-universal:font-body",
            "theme-universal:rounded-input",
            "theme-universal:hover:border-base-focus",
            widthStyles,
            disabledStyles,
            className,
          )}
        >
          <div className={cn("flex items-center space-x-3 min-w-0", !compact && "flex-1")}>
            {selectedCountry ? (
              <>
                {!compact && (
                  <div className="flex-shrink-0 w-6 h-4 flex items-center justify-center">
                    <span className="text-font-base">{selectedCountry.flag}</span>
                  </div>
                )}
                <div className={cn("min-w-0", !compact && "flex-1")}>
                  <span className="theme-universal:text-input-text theme-universal:font-body truncate">
                    {compact
                      ? selectedCountry.dialCode
                      : `${selectedCountry.name}, ${selectedCountry.code}, ${selectedCountry.dialCode}`}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex-shrink-0 w-6 h-4 theme-universal:bg-input-border rounded-sm"></div>
                <span className="theme-universal:text-input-labels flex-1 truncate">
                  {placeholder}
                </span>
              </>
            )}
          </div>
          <ChevronDown className="w-4 h-4 theme-universal:text-input-labels shrink-0" />
        </ULThemeButton>

        <Combobox.Portal container={containerRef}>
          <Combobox.Positioner
            className="w-[400px]"
            side="bottom"
            align="start"
            sideOffset={4}
            collisionAvoidance={{ side: "none" }}
          >
            <Combobox.Popup>
              <Combobox.Input placeholder={searchPlaceholder} autoFocus />
              <Combobox.Empty>No countries found</Combobox.Empty>
              <Combobox.List>
                {(item: CountryCodePickerData) => (
                  <Combobox.Item key={item.code} value={item}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0 w-6 text-center">{item.flag}</span>
                      <span className="truncate">{item.name}</span>
                    </span>
                    <span className="text-gray-500 font-mono shrink-0">{item.dialCode}</span>
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </div>
  );
}

export default ULThemeCountryCodeDropdown;
